import type { SimCore, RacerInput } from '@/sim/SimCore';
import { ClientPredictor, type ReplayMode } from '@/net/ClientPredictor';
import { clamp, shortestAngleDelta } from '@/math/angles';
import { netdiag } from '@/net/netdiag';
import type { Snapshot } from '@/net/protocol';
import type { CarRenderSnapshot } from '@/entities/Car';
import type { BallRenderSnapshot } from '@/entities/Ball';

/** A per-racer pose ready to hand to the views. */
export interface RacerPose {
  id: number;
  isLocal: boolean;
  car: CarRenderSnapshot;
  ball: BallRenderSnapshot;
}

/** How fast a correction decays to zero (per second, exponential) on a clean
 *  link; on a rough one we ease slower so repeated corrections read as drift,
 *  not vibration (adaptive smoothing, M52). ONE rate pair for every racer:
 *  M69 tried easing rivals slower (10/7) and owner QA read the under-damped
 *  refolds as cars "sliding left and right" — AI rivals steer constantly, so
 *  their 20 Hz corrections need the tight damping back (M71). */
const SMOOTH_RATE_CLEAN = 16;
const SMOOTH_RATE_ROUGH = 9;
/** Corrections above this EMA magnitude count the link as "rough". */
const ROUGH_CORRECTION = 0.9;
/** "Glide, don't teleport" (M69, hardened M105): the glide budget scales with
 *  the entity's own speed — the old fixed 6 u cap was only ~130 ms of travel
 *  at race pace, so every link hiccup at speed HARD-SNAPPED the car (the
 *  frozen-reverse yank). Corrections up to the cap glide out via the render
 *  offset; a correction BEYOND the cap is clamped TO it (M105) — the entity
 *  hops only the excess and glides the rest, where the old code zeroed the
 *  whole offset and rendered the entire correction as one raw teleport. */
const SNAP_BASE = 6; // u — floor: parked cars keep the old behaviour
const SNAP_PER_SPEED = 0.5; // u of tolerated glide per u/s of speed
const SNAP_HARD = 30; // u — the glide budget's ceiling

/** Exported for the unit tests — the tuning story lives in the constants above. */
export const correctionSnapCap = (speed: number): number =>
  clamp(speed * SNAP_PER_SPEED, SNAP_BASE, SNAP_HARD);

/** Pre-change pose fingerprints, for folding a sim change into render offsets. */
interface PoseMark {
  id: number;
  cx: number;
  cy: number;
  cz: number;
  ch: number;
  bx: number;
  by: number;
  bz: number;
}

/**
 * ClientSession.ts — the view-facing client. With full-world prediction every
 * racer (local AND rivals) comes straight from the prediction sim, so they're all
 * at "now" — no past-interpolation, which is what made collisions line up. The one
 * job here is to keep CORRECTIONS smooth: when a snapshot moves any racer, the
 * delta is folded into that entity's render-only offset and decayed over ~100ms,
 * so cars/balls — and the camera following the local car — ease to the truth
 * instead of snapping. Offsets are 0 offline, so solo rendering is untouched.
 */
export class ClientSession {
  readonly predictor: ClientPredictor;
  /** EMA of the local car's correction size — the link-quality signal. */
  private correctionEma = 0;

  constructor(
    sim: SimCore,
    private readonly localId: number,
    private readonly dt: number,
    /** How the HOST applies inputs (see ClientPredictor's ReplayMode): the
     *  real servers schedule them by label; the in-tab loopback applies them
     *  on arrival. */
    mode: ReplayMode = 'ack',
  ) {
    this.predictor = new ClientPredictor(sim, localId, mode);
  }

  predict(input: RacerInput): void {
    this.predictor.predict(input, this.dt);
  }

  /** M71: a clock-sync EXTRA tick (the dilation "double") advances the whole
   *  world 8.3 ms inside one render frame — real sim time, but rendered raw it
   *  reads as a tiny position hop, and the race-start convergence burst fires
   *  a stream of them. Fold the extra motion into the render offsets so it
   *  glides out through the same easing every correction uses. */
  predictExtra(input: RacerInput): void {
    const before = this.capturePoses();
    this.predictor.predict(input, this.dt);
    this.foldFrom(before);
  }

  private capturePoses(): PoseMark[] {
    return this.predictor.racers.map((r) => ({
      id: r.id,
      cx: r.car.state.x,
      cy: r.car.y,
      cz: r.car.state.z,
      ch: r.car.state.heading,
      bx: r.ball.state.x,
      by: r.ball.state.y,
      bz: r.ball.state.z,
    }));
  }

  /** Fold each racer's movement since `before` into its decaying render offset
   *  (rendered pose stays continuous; the offset eases to zero). Oversized
   *  offsets are CLAMPED to the speed-relative glide budget (M105) — never
   *  zeroed: zeroing rendered the whole correction as one raw teleport, which
   *  is exactly the artifact players report. Clamping hops only the excess. */
  private foldFrom(before: PoseMark[]): void {
    for (const b of before) {
      const r = this.predictor.racers.find((x) => x.id === b.id);
      if (!r) continue;
      r.car.rOffX += b.cx - r.car.state.x;
      r.car.rOffY += b.cy - r.car.y;
      r.car.rOffZ += b.cz - r.car.state.z;
      r.car.rOffH += shortestAngleDelta(r.car.state.heading, b.ch);
      r.ball.rOffX += b.bx - r.ball.state.x;
      r.ball.rOffY += b.by - r.ball.state.y;
      r.ball.rOffZ += b.bz - r.ball.state.z;
      const carSpeed = Math.hypot(r.car.state.vx, r.car.state.vz);
      const carCap = correctionSnapCap(carSpeed);
      const carMag = Math.hypot(r.car.rOffX, r.car.rOffZ);
      if (carMag > carCap) {
        const s = carCap / carMag;
        r.car.rOffX *= s;
        r.car.rOffY *= s;
        r.car.rOffZ *= s;
        r.car.rOffH *= s;
        if (b.id === this.localId) netdiag.corrSnapped++; // still a visible hop (of the excess)
      }
      const ballSpeed = Math.hypot(r.ball.state.vx, r.ball.state.vz);
      const ballCap = correctionSnapCap(ballSpeed);
      const ballMag = Math.hypot(r.ball.rOffX, r.ball.rOffZ);
      if (ballMag > ballCap) {
        const s = ballCap / ballMag;
        r.ball.rOffX *= s;
        r.ball.rOffY *= s;
        r.ball.rOffZ *= s;
      }
    }
  }

  /** Reconcile, then fold each racer's correction into its decaying render offset. */
  applySnapshot(snap: Snapshot): void {
    const before = this.capturePoses();
    this.predictor.reconcile(snap, this.dt);
    const me = before.find((b) => b.id === this.localId);
    const local = me && this.predictor.racers.find((x) => x.id === this.localId);
    if (me && local) {
      const mag = Math.hypot(me.cx - local.car.state.x, me.cz - local.car.state.z);
      this.correctionEma = this.correctionEma * 0.85 + mag * 0.15;
      // Diagnostics (M72): how big was THIS reconcile's local-car correction?
      netdiag.corrLast = mag;
      if (mag > netdiag.corrMax) netdiag.corrMax = mag;
      if (mag < 0.05) netdiag.corrClean++;
      else if (mag < 1) netdiag.corrSmall++;
      else if (mag < 6) netdiag.corrMed++;
      else netdiag.corrBig++;
    }
    this.foldFrom(before);
  }

  /** Render poses for this frame; decays every offset, so call once per frame. */
  poses(alpha: number, frameDt: number): RacerPose[] {
    const rate = this.correctionEma > ROUGH_CORRECTION ? SMOOTH_RATE_ROUGH : SMOOTH_RATE_CLEAN;
    const k = Math.exp(-rate * frameDt);
    return this.predictor.racers.map((r) => {
      r.car.rOffX *= k;
      r.car.rOffY *= k;
      r.car.rOffZ *= k;
      r.car.rOffH *= k;
      r.ball.rOffX *= k;
      r.ball.rOffY *= k;
      r.ball.rOffZ *= k;
      return {
        id: r.id,
        isLocal: r.id === this.localId,
        car: r.car.snapshot(alpha),
        ball: r.ball.snapshot(alpha),
      };
    });
  }
}
